// How long one play of a GIF file lasts, read from the file's own frame delays (#529). A browser animates an
// uploaded GIF by itself and loops it; to stop it after the number of plays the GIF plays setting asks for (gifPlays.ts),
// the page needs the length of one play. The browser's own rule for a delay applies: 10 ms or less is drawn as 100 ms.
// An animated WebP, KLIPY's usual picture format (#589), is read the same way (webpFrameDelays).

/** The delay of each frame in ms, in order; null when the bytes are not a GIF or it has fewer than two frames. */
export function gifFrameDelays(b: Uint8Array): number[] | null {
  if (b.length < 13 || b[0] !== 0x47 || b[1] !== 0x49 || b[2] !== 0x46) return null;
  const skipTable = (at: number, flags: number) => (flags & 0x80 ? at + 3 * (1 << ((flags & 7) + 1)) : at);
  const skipBlocks = (at: number) => {
    while (at < b.length && b[at] !== 0) at += b[at]! + 1;
    return at + 1;
  };
  const delays: number[] = [];
  let at = skipTable(13, b[10]!);
  let delay = 0;
  while (at < b.length) {
    const kind = b[at]!;
    if (kind === 0x21) {
      // An extension. A graphic control extension (0xF9) holds the delay of the frame that follows.
      if (b[at + 1] === 0xf9 && at + 6 < b.length) delay = b[at + 4]! | (b[at + 5]! << 8);
      at = skipBlocks(at + 2);
    } else if (kind === 0x2c) {
      // A frame: its descriptor (10 bytes), maybe a colour table, the code size, then the data blocks.
      delays.push(delay <= 1 ? 100 : delay * 10);
      delay = 0;
      at = skipBlocks(skipTable(at + 10, b[at + 9]!) + 1);
    } else {
      break; // 0x3B is the end; anything else is a broken file
    }
  }
  return delays.length > 1 ? delays : null;
}

/** When to swap a GIF set to `plays` plays for its still: the middle of the last frame of the last play, in ms. */
export function gifStopAfter(delays: number[], plays: number): number {
  const play = delays.reduce((sum, d) => sum + d, 0);
  return play * plays - delays[delays.length - 1]! / 2;
}

/** The delay of each frame of an animated WebP in ms, in order; null when the bytes are not an animated WebP or it has fewer than two frames. */
export function webpFrameDelays(b: Uint8Array): number[] | null {
  const tag = (at: number, s: string) => [...s].every((c, i) => b[at + i] === c.charCodeAt(0));
  if (b.length < 12 || !tag(0, "RIFF") || !tag(8, "WEBP")) return null;
  const delays: number[] = [];
  // A chunk is a four-letter name, a 32-bit little-endian size, the payload, and one pad byte when the size is odd.
  for (let at = 12; at + 8 <= b.length; ) {
    const size = (b[at + 4]! | (b[at + 5]! << 8) | (b[at + 6]! << 16) | (b[at + 7]! << 24)) >>> 0;
    // An ANMF chunk (one frame) starts with x, y, width - 1 and height - 1 (3 bytes each), then the duration (3 bytes).
    if (tag(at, "ANMF")) {
      if (size < 16 || at + 8 + 15 > b.length) return null;
      const d = b[at + 8 + 12]! | (b[at + 8 + 13]! << 8) | (b[at + 8 + 14]! << 16);
      delays.push(d <= 10 ? 100 : d);
    }
    at += 8 + size + (size & 1);
  }
  return delays.length > 1 ? delays : null;
}

/** The frame delays of a GIF or an animated WebP, with the type the browser's ImageDecoder needs; null for anything else. */
export function pictureFrameDelays(b: Uint8Array): { type: "image/gif" | "image/webp"; delays: number[] } | null {
  const gif = gifFrameDelays(b);
  if (gif) return { type: "image/gif", delays: gif };
  const webp = webpFrameDelays(b);
  return webp ? { type: "image/webp", delays: webp } : null;
}
